import { useMemo } from 'react';

const SNAP_THRESHOLD = 8; // Pixels to snap within
const GUIDE_COLORS = {
  center: '#3B82F6', // Blue for center alignment
  edge: '#10B981',   // Green for edge alignment
};

/**
 * Calculate alignment guides when dragging objects
 * Returns guide lines to display and snap positions
 */
export const useAlignmentGuides = (
  draggedObject,
  allObjects,
  isDragging,
  zoom = 1
) => {
  const guides = useMemo(() => {
    if (!isDragging || !draggedObject) {
      return { horizontal: [], vertical: [], snapX: null, snapY: null };
    }

    const horizontalGuides = [];
    const verticalGuides = [];
    let snapX = null;
    let snapY = null;

    const draggedBounds = {
      left: draggedObject.x,
      right: draggedObject.x + (draggedObject.width || 100),
      top: draggedObject.y,
      bottom: draggedObject.y + (draggedObject.height || 60),
      centerX: draggedObject.x + (draggedObject.width || 100) / 2,
      centerY: draggedObject.y + (draggedObject.height || 60) / 2,
    };

    // Check alignment with each other object
    for (const obj of allObjects) {
      if (obj.id === draggedObject.id) continue;
      if (obj.type === 'connector' || obj.type === 'drawing') continue;

      const objBounds = {
        left: obj.x,
        right: obj.x + (obj.width || 100),
        top: obj.y,
        bottom: obj.y + (obj.height || 60),
        centerX: obj.x + (obj.width || 100) / 2,
        centerY: obj.y + (obj.height || 60) / 2,
      };

      const threshold = SNAP_THRESHOLD / zoom;

      // Vertical alignments (x-axis)
      // Left edge to left edge
      if (Math.abs(draggedBounds.left - objBounds.left) < threshold) {
        verticalGuides.push({ x: objBounds.left, type: 'edge' });
        if (snapX === null) snapX = objBounds.left;
      }
      // Right edge to right edge
      if (Math.abs(draggedBounds.right - objBounds.right) < threshold) {
        verticalGuides.push({ x: objBounds.right, type: 'edge' });
        if (snapX === null) snapX = objBounds.right - (draggedObject.width || 100);
      }
      // Left to right
      if (Math.abs(draggedBounds.left - objBounds.right) < threshold) {
        verticalGuides.push({ x: objBounds.right, type: 'edge' });
        if (snapX === null) snapX = objBounds.right;
      }
      // Right to left
      if (Math.abs(draggedBounds.right - objBounds.left) < threshold) {
        verticalGuides.push({ x: objBounds.left, type: 'edge' });
        if (snapX === null) snapX = objBounds.left - (draggedObject.width || 100);
      }
      // Center to center (vertical)
      if (Math.abs(draggedBounds.centerX - objBounds.centerX) < threshold) {
        verticalGuides.push({ x: objBounds.centerX, type: 'center' });
        if (snapX === null) snapX = objBounds.centerX - (draggedObject.width || 100) / 2;
      }

      // Horizontal alignments (y-axis)
      // Top edge to top edge
      if (Math.abs(draggedBounds.top - objBounds.top) < threshold) {
        horizontalGuides.push({ y: objBounds.top, type: 'edge' });
        if (snapY === null) snapY = objBounds.top;
      }
      // Bottom edge to bottom edge
      if (Math.abs(draggedBounds.bottom - objBounds.bottom) < threshold) {
        horizontalGuides.push({ y: objBounds.bottom, type: 'edge' });
        if (snapY === null) snapY = objBounds.bottom - (draggedObject.height || 60);
      }
      // Top to bottom
      if (Math.abs(draggedBounds.top - objBounds.bottom) < threshold) {
        horizontalGuides.push({ y: objBounds.bottom, type: 'edge' });
        if (snapY === null) snapY = objBounds.bottom;
      }
      // Bottom to top
      if (Math.abs(draggedBounds.bottom - objBounds.top) < threshold) {
        horizontalGuides.push({ y: objBounds.top, type: 'edge' });
        if (snapY === null) snapY = objBounds.top - (draggedObject.height || 60);
      }
      // Center to center (horizontal)
      if (Math.abs(draggedBounds.centerY - objBounds.centerY) < threshold) {
        horizontalGuides.push({ y: objBounds.centerY, type: 'center' });
        if (snapY === null) snapY = objBounds.centerY - (draggedObject.height || 60) / 2;
      }
    }

    // Remove duplicates
    const uniqueHorizontal = horizontalGuides.filter(
      (g, i, arr) => arr.findIndex(x => Math.abs(x.y - g.y) < 1) === i
    );
    const uniqueVertical = verticalGuides.filter(
      (g, i, arr) => arr.findIndex(x => Math.abs(x.x - g.x) < 1) === i
    );

    return {
      horizontal: uniqueHorizontal,
      vertical: uniqueVertical,
      snapX,
      snapY,
    };
  }, [draggedObject, allObjects, isDragging, zoom]);

  return guides;
};

/**
 * Render alignment guide lines on the canvas
 */
export const AlignmentGuides = ({ 
  guides, 
  zoom, 
  pan, 
  canvasWidth, 
  canvasHeight 
}) => {
  if (!guides || (guides.horizontal.length === 0 && guides.vertical.length === 0)) {
    return null;
  }

  return (
    <g className="alignment-guides pointer-events-none">
      {/* Vertical guides (x-axis alignment) */}
      {guides.vertical.map((guide, index) => (
        <line
          key={`v-${index}`}
          x1={guide.x}
          y1={-10000}
          x2={guide.x}
          y2={10000}
          stroke={guide.type === 'center' ? GUIDE_COLORS.center : GUIDE_COLORS.edge}
          strokeWidth={1 / zoom}
          strokeDasharray={guide.type === 'center' ? `${4 / zoom} ${4 / zoom}` : 'none'}
          opacity={0.8}
        />
      ))}

      {/* Horizontal guides (y-axis alignment) */}
      {guides.horizontal.map((guide, index) => (
        <line
          key={`h-${index}`}
          x1={-10000}
          y1={guide.y}
          x2={10000}
          y2={guide.y}
          stroke={guide.type === 'center' ? GUIDE_COLORS.center : GUIDE_COLORS.edge}
          strokeWidth={1 / zoom}
          strokeDasharray={guide.type === 'center' ? `${4 / zoom} ${4 / zoom}` : 'none'}
          opacity={0.8}
        />
      ))}
    </g>
  );
};

/**
 * Apply snap position to object during drag
 */
export const applySnap = (position, guides, enabled = true) => {
  if (!enabled) return position;

  return {
    x: guides.snapX !== null ? guides.snapX : position.x,
    y: guides.snapY !== null ? guides.snapY : position.y,
  };
};
